import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  X, LayoutDashboard, Share2, Globe, Star, PenTool, Search, Shield,
  BarChart2, Megaphone, Mail, Settings, Users, Activity, Eye, Mic2,
  Target, ShoppingBag, Heart, Headphones, Briefcase, ChevronDown,
  ChevronRight, Zap, ScanLine, Award, Gift, UserPlus, MessageSquare,
  FileText, CalendarDays, FolderOpen, Bell, TrendingUp, Phone,
  Ticket, BookOpen, ClipboardList, GanttChart, Lock, BarChart3,
  QrCode, CheckSquare, Receipt, Inbox, Truck, Crosshair, Calendar,
  AlertTriangle, Crown, BarChart, Bot, PhoneIncoming, Timer,
  MousePointer, FlaskConical, Video, Link2, Gauge, Key, Coins, Palette, Sparkles, PieChart, ImageIcon,
  HeartPulse, MapPin, Building2, Blocks, Store, Clock, Sliders, Code2, Presentation, Wallet, Percent, UserCircle, Umbrella, DollarSign, GraduationCap, Rocket, Warehouse, Box, ShieldCheck, Database, Radio,
  Grid3x3, Route as RouteIcon, Scale, Tv2, PlayCircle} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useModules, isModuleEnabled, getModuleState } from '../../hooks/useModules';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { PlanBadge } from '../PlanBadge';
import { cn } from '../../lib/utils';
import { PLATFORM_VERSION } from '../../config/version';

// ─── Role hierarchy (cascading access) ───
const ALL = ['super_admin', 'country_operator', 'client_admin', 'team_member'];
const MANAGERS = ['super_admin', 'country_operator', 'client_admin'];
const TENANTS = ['country_operator', 'client_admin']; // exclude super_admin — they don't subscribe
const ADMIN = ['super_admin', 'country_operator'];
const PLATFORM = ['super_admin'];
const EMPLOYEE = ['team_member']; // v3.13 — self-service portal (My Dashboard, My Profile)

// ─── Audience tags (what each role NEEDS in their default landing) ───
// Items tagged 'platform' appear in Super Admin's clean default view.
// Items tagged 'operator' appear in Country Operator's clean default view.
// Items without audiences are treated as subscriber-facing → hidden from
// Super Admin / Country Operator unless they toggle "Show all features".
// Subscribers (client_admin / team_member) ALWAYS see everything they have
// role-access to (no audience filtering applied) — they're the ones who
// actually USE the platform, so there is no clutter to hide from them.
const PLAT_AUDIENCE = ['platform'];
const OP_AUDIENCE = ['operator'];
const BOTH_ADMIN_AUDIENCE = ['platform', 'operator'];

// ─── Section definitions ───
const sidebarSections = [
  // v3.13 — Employee self-service portal. Only visible to team_member role.
  // The MyDashboardPage / MyProfilePage components existed in the repo for
  // weeks but were never linked from anywhere — this section is the
  // access point that makes 'employee access' actually work in production.
  {
    id: 'my_portal',
    label: 'My Portal',
    icon: UserCircle,
    color: '#10B981',
    items: [
      { path: '/employee/dashboard', icon: LayoutDashboard, label: 'My Dashboard', roles: EMPLOYEE },
      { path: '/employee/attendance', icon: Clock, label: 'Attendance & Time-off', roles: EMPLOYEE },
      { path: '/employee/leave', icon: Umbrella, label: 'Leave Requests', roles: EMPLOYEE },
      { path: '/employee/payroll', icon: DollarSign, label: 'Paystubs', roles: EMPLOYEE },
      { path: '/employee/benefits', icon: Heart, label: 'Benefits', roles: EMPLOYEE },
      { path: '/employee/goals', icon: Target, label: 'My Goals & KPIs', roles: EMPLOYEE },
      { path: '/employee/training', icon: GraduationCap, label: 'Training', roles: EMPLOYEE },
      { path: '/employee/expenses', icon: Receipt, label: 'Expenses', roles: EMPLOYEE },
      { path: '/employee/profile', icon: UserCircle, label: 'My Profile', roles: EMPLOYEE },
    ],
  },
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    color: '#10B981',
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: MANAGERS, audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/command-center', icon: Gauge, label: 'Command Center', roles: ADMIN, isNew: true, moduleId: 'command_center', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/settings/module-manager', icon: Blocks, label: 'Module Management', roles: MANAGERS, isNew: true, audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/admin', icon: Users, label: 'Admin Portal', roles: PLATFORM, moduleId: 'admin_portal', audiences: PLAT_AUDIENCE },
      { path: '/settings/oauth-apps', icon: Key, label: 'OAuth Apps', roles: PLATFORM, audiences: PLAT_AUDIENCE },
      { path: '/overview/onboarding', icon: CheckSquare, label: 'Onboarding Checklist', roles: MANAGERS, audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/settings/ad-accounts', icon: Link2, label: 'Ad Accounts', roles: ADMIN, audiences: OP_AUDIENCE },
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    icon: Zap,
    color: '#10B981',
    items: [
      { path: '/meeting', icon: Video, label: 'KonnectMeet', roles: ALL, isNew: true, moduleId: 'konnect_meet' },
      { path: '/prompt-powerup', icon: Zap, label: 'Prompt PowerUp', roles: ALL, isNew: true, moduleId: 'prompt_powerup' },
      { path: '/tools/template-library', icon: FileText, label: 'Template Library', roles: MANAGERS, isNew: true, moduleId: 'template_library' },
    ],
  },
  {
    id: 'leads',
    label: 'Lead Generation',
    icon: Target,
    color: '#3B82F6',
    items: [
      { path: '/leads/pipeline', icon: Target, label: 'Contacts & Leads', roles: MANAGERS, moduleId: 'contacts' },
      { path: '/leads/capture-forms', icon: FileText, label: 'Capture Forms', roles: MANAGERS, moduleId: 'capture_forms' },
      { path: '/leads/card-scanner', icon: ScanLine, label: 'Contact & QR Scanner', roles: ALL, moduleId: 'contact_scanner' },
      { path: '/leads/ai-insights', icon: TrendingUp, label: 'Lead Scoring & AI', roles: MANAGERS, moduleId: 'lead_scoring' },
      { path: '/leads/automation', icon: Zap, label: 'Lead Routing & Automation', roles: MANAGERS, isNew: true, moduleId: 'lead_automation' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: ShoppingBag,
    color: '#8B5CF6',
    items: [
      { path: '/sales/clients', icon: Building2, label: 'Clients', roles: ALL, moduleId: 'clients' },
      { path: '/booking', icon: Calendar, label: 'Booking', roles: ALL, isNew: true, moduleId: 'booking' },
      { path: '/sales/catalog', icon: ShoppingBag, label: 'Products & Services', roles: ALL, moduleId: 'product_catalog' },
      { path: '/marketplace', icon: Store, label: 'Public Marketplace', roles: ALL },
      { path: '/sales/inquiries', icon: Inbox, label: 'Inquiry Inbox', roles: MANAGERS, moduleId: 'inquiry_inbox' },
      { path: '/sales/proposals', icon: FileText, label: 'Proposals & Quotes', roles: MANAGERS, moduleId: 'proposals' },
      { path: '/sales/pipeline', icon: TrendingUp, label: 'Sales Pipeline', roles: MANAGERS, moduleId: 'sales_pipeline' },
      { path: '/sales/commissions', icon: Award, label: 'Commission Tracker', roles: MANAGERS, moduleId: 'commissions' },
      { path: '/sales-deck', icon: Presentation, label: 'Sales Deck Studio', roles: ADMIN, isNew: true },
      { path: '/sales/invoices', icon: Receipt, label: 'Invoices', roles: MANAGERS, moduleId: 'invoices' },
      { path: '/sales/e-signature', icon: PenTool, label: 'E-Signature', roles: MANAGERS, moduleId: 'esignature' },
      { path: '/sales/follow-ups', icon: Bell, label: 'Follow-Up Reminders', roles: ALL, moduleId: 'followups' },
      { path: '/sales/promotions', icon: Gift, label: 'Promotions & Deals', roles: MANAGERS, moduleId: 'promotions' },
    ],
  },
  {
    id: 'vending',
    label: 'Vending Fleet',
    icon: Warehouse,
    color: '#0EA5E9',
    hideWhenAllLocked: true, // v2.5.867 — Vending is a paid add-on; hide the entire section for subscribers who haven't purchased it (super_admin & purchasers always see it)
    items: [
      { path: '/vending', icon: LayoutDashboard, label: 'Dashboard', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/machines', icon: Box, label: 'Machines', roles: MANAGERS, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/locations', icon: MapPin, label: 'Locations', roles: MANAGERS, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/clients', icon: Building2, label: 'Clients', roles: MANAGERS, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/products', icon: ShoppingBag, label: 'Products', roles: MANAGERS, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/telemetry', icon: Activity, label: 'Telemetry', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/planograms', icon: LayoutDashboard, label: 'Planograms', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/planogram-editor', icon: Grid3x3, label: 'Planogram Editor', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/routing', icon: RouteIcon, label: 'Routing & Pre-Kit', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/reconciliation', icon: Scale, label: 'Reconciliation', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/dashboard-tv', icon: Tv2, label: 'Kiosk TV', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/vending/companion', icon: Radio, label: 'DEX Companion', roles: MANAGERS, isNew: true, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE, companionOnly: true },
      // v2.5.897 — Replay Vending Tour lives inside the Vending sidebar so
      // subscribers don't need to leave the module to find it. `action` marks
      // this as a click-only item (no route). Handler dispatched below.
      { action: 'replay-vending-tour', icon: PlayCircle, label: 'Replay Tour', roles: MANAGERS, moduleId: 'vending', audiences: BOTH_ADMIN_AUDIENCE },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: Wallet,
    items: [
      { path: '/finance', icon: LayoutDashboard, label: 'Finance Dashboard', roles: MANAGERS, isNew: true, moduleId: 'finance_dashboard' },
      { path: '/finance/expenses', icon: Receipt, label: 'Expenses', roles: MANAGERS, isNew: true, moduleId: 'finance_expenses' },
      { path: '/finance/bills', icon: FileText, label: 'Bills & Vendors', roles: MANAGERS, isNew: true, moduleId: 'finance_bills' },
      { path: '/finance/reports', icon: BarChart3, label: 'P&L / Balance Sheet', roles: MANAGERS, isNew: true, moduleId: 'finance_reports' },
      { path: '/finance/tax', icon: Percent, label: 'Tax & VAT', roles: MANAGERS, isNew: true, moduleId: 'finance_tax' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    color: '#F97316',
    items: [
      { path: '/social', icon: Share2, label: 'Social Media', roles: MANAGERS, moduleId: 'social_media' },
      { path: '/content-queue', icon: Sparkles, label: 'Content Queue', roles: MANAGERS, isNew: true, moduleId: 'content_queue' },
      { path: '/marketing/brand-voice', icon: Sparkles, label: 'Brand Voice', roles: MANAGERS, isNew: true, moduleId: 'brand_voice' },
      { path: '/influencer', icon: Sparkles, label: 'AI Influencer Studio', roles: MANAGERS, isNew: true, moduleId: 'influencer' },
      { path: '/content', icon: PenTool, label: 'Creative Studio', roles: MANAGERS, moduleId: 'creative_studio' },
      { path: '/content/media', icon: ImageIcon, label: 'Media Library', roles: MANAGERS, isNew: true, moduleId: 'media_library', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/email', icon: Mail, label: 'Email Marketing', roles: MANAGERS, moduleId: 'email_marketing' },
      { path: '/ads', icon: Megaphone, label: 'Paid Ads', roles: MANAGERS, moduleId: 'paid_ads' },
      { path: '/seo', icon: Search, label: 'SEO Center', roles: MANAGERS, moduleId: 'seo_center' },
      { path: '/marketing/messaging', icon: MessageSquare, label: 'Messaging Channels', roles: MANAGERS, moduleId: 'messaging' },
      { path: '/marketing/affiliates', icon: UserPlus, label: 'Influencer & Affiliates', roles: MANAGERS, moduleId: 'affiliates' },
      { path: '/marketing/events', icon: CalendarDays, label: 'Events & Webinars', roles: MANAGERS, moduleId: 'events' },
      { path: '/marketing/campaign-calendar', icon: Calendar, label: 'Campaign Calendar', roles: MANAGERS, moduleId: 'campaign_calendar' },
    ],
  },
  {
    id: 'retention',
    label: 'Customer Retention',
    icon: Heart,
    color: '#EC4899',
    items: [
      { path: '/retention/loyalty', icon: Award, label: 'Loyalty Program', roles: MANAGERS, moduleId: 'loyalty' },
      { path: '/retention/celebrations', icon: Gift, label: 'Birthday & Anniversary', roles: MANAGERS, moduleId: 'celebrations' },
      { path: '/retention/referrals', icon: UserPlus, label: 'Referral Program', roles: MANAGERS, moduleId: 'referrals' },
      { path: '/retention/winback', icon: Heart, label: 'Win-Back Campaigns', roles: MANAGERS, moduleId: 'winback' },
      { path: '/retention/churn-prediction', icon: AlertTriangle, label: 'Churn Prediction', roles: ADMIN, moduleId: 'churn' },
      { path: '/retention/vip-tiers', icon: Crown, label: 'VIP Tiers', roles: MANAGERS, moduleId: 'vip_tiers' },
    ],
  },
  {
    id: 'reputation',
    label: 'Reputation',
    icon: Star,
    color: '#F59E0B',
    items: [
      { path: '/reputation', icon: Star, label: 'Reviews Inbox', roles: MANAGERS, isNew: true, moduleId: 'reputation' },
      { path: '/reputation?tab=sources', icon: Globe, label: 'Connected Sources', roles: MANAGERS, isNew: true },
      { path: '/reputation?tab=settings', icon: Settings, label: 'Auto-Reply Settings', roles: MANAGERS, isNew: true },
      { path: '/reputation?tab=setup', icon: BookOpen, label: 'Setup Guide', roles: MANAGERS, isNew: true },
    ],
  },
  {
    id: 'service',
    label: 'Customer Service',
    icon: Headphones,
    color: '#14B8A6',
    items: [
      { path: '/voice-receptionist', icon: Mic2, label: 'Voice Receptionist', roles: MANAGERS, enterprise: true, moduleId: 'voice_receptionist' },
      { path: '/service/helpdesk', icon: Ticket, label: 'Help Desk & Ticketing', roles: ALL, moduleId: 'helpdesk' },
      { path: '/whatsapp', icon: MessageSquare, label: 'WhatsApp Business', roles: MANAGERS, isNew: true, moduleId: 'whatsapp' },
      { path: '/service/live-chat', icon: MessageSquare, label: 'Live Chat', roles: ALL, moduleId: 'live_chat' },
      { path: '/service/knowledge-base', icon: BookOpen, label: 'Knowledge Base', roles: ALL, moduleId: 'knowledge_base' },
      { path: '/service/surveys', icon: ClipboardList, label: 'Surveys & Feedback', roles: MANAGERS, moduleId: 'surveys' },
      { path: '/service/inbox', icon: Inbox, label: 'Omnichannel Inbox', roles: MANAGERS, moduleId: 'omnichannel' },
      { path: '/service/ai-chatbot', icon: Bot, label: 'AI Chatbot', roles: ADMIN, moduleId: 'ai_chatbot' },
      { path: '/service/call-recordings', icon: PhoneIncoming, label: 'Call Recording', roles: ADMIN, moduleId: 'call_recording' },
      { path: '/service/sla', icon: Timer, label: 'SLA Management', roles: ADMIN, moduleId: 'sla_mgmt' },
    ],
  },
  {
    id: 'hr',
    label: 'Human Resources',
    icon: Users,
    color: '#8B5CF6',
    items: [
      { path: '/operations/staff', icon: UserPlus, label: 'Team & People', roles: MANAGERS, moduleId: 'staff_mgmt' },
      { path: '/operations/attendance', icon: Clock, label: 'Attendance & Payroll', roles: MANAGERS, moduleId: 'attendance' },
      { path: '/operations/evaluations', icon: Award, label: 'Performance & KPIs', roles: MANAGERS, moduleId: 'evaluations' },
      { path: '/hr/escalation', icon: AlertTriangle, label: 'Escalation & 201 File', roles: MANAGERS, isNew: true, moduleId: 'escalation_201' },
      { path: '/hr/kpi-dashboard', icon: TrendingUp, label: 'KPI Dashboard', roles: MANAGERS, isNew: true, moduleId: 'kpi_dashboard' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Briefcase,
    color: '#F59E0B',
    items: [
      { path: '/operations/tasks', icon: ClipboardList, label: 'Tasks & Projects', roles: ALL, moduleId: 'tasks' },
      { path: '/operations/calendar', icon: CalendarDays, label: 'Calendar', roles: ALL, moduleId: 'calendar' },
      { path: '/operations/documents', icon: FolderOpen, label: 'Document Vault', roles: ALL, moduleId: 'document_vault' },
      // v2.5.897 — Workflow Automation merged into Automation Center (Settings → Automation Center).
      // Kept moduleId 'workflows' entry pointing at the consolidated engine so any bookmarked links resolve.
      { path: '/automation-center', icon: GanttChart, label: 'Automation Center', roles: MANAGERS, moduleId: 'workflows' },
      // v2.5.895 — Automation Score sidebar entry removed. The dashboard page
      // was throwing an error toast on load for tenants that don't have any
      // automations yet, and the metric duplicates what Automation Center
      // already shows on its overview tab. Kept the route so existing links
      // still work.
      // { path: '/operations/automation', icon: Zap, label: 'Automation Score', roles: MANAGERS, isNew: true, moduleId: 'automation_score' },
      { path: '/operations/announcements', icon: Bell, label: 'Announcements', roles: ALL, moduleId: 'announcements' },
      { path: '/operations/field-jobs', icon: Briefcase, label: 'Field Jobs', roles: MANAGERS, isNew: true, moduleId: 'field_jobs' },
      { path: '/operations/field-gps', icon: MapPin, label: 'GPS Live Map', roles: MANAGERS, isNew: true, moduleId: 'field_gps' },
      { path: '/operations/field-schedule', icon: Calendar, label: 'Field Schedule', roles: MANAGERS, isNew: true, moduleId: 'field_schedule' },
      { path: '/operations/alert-management', icon: AlertTriangle, label: 'Alert Management', roles: ADMIN, isNew: true, moduleId: 'alerts' },
    ],
  },
  {
    id: 'website',
    label: 'Website & Compliance',
    icon: Globe,
    color: '#6366F1',
    items: [
      { path: '/website', icon: Globe, label: 'Website Manager', roles: MANAGERS, moduleId: 'website_mgr', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/ada', icon: Shield, label: 'ADA Compliance', roles: MANAGERS, moduleId: 'ada', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/website/landing-pages', icon: FileText, label: 'Landing Page Builder', roles: MANAGERS, moduleId: 'landing_pages', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/website/heatmaps', icon: MousePointer, label: 'Heatmaps', roles: MANAGERS, moduleId: 'heatmaps', audiences: BOTH_ADMIN_AUDIENCE },
      { path: '/website/cro', icon: FlaskConical, label: 'CRO & A/B Testing', roles: MANAGERS, moduleId: 'cro', audiences: BOTH_ADMIN_AUDIENCE },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics & Reports',
    icon: BarChart2,
    color: '#06B6D4',
    items: [
      { path: '/analytics', icon: BarChart2, label: 'Live Dashboard', roles: MANAGERS, moduleId: 'analytics' },
      { path: '/analytics/report-dashboard', icon: PieChart, label: 'Report Dashboard', roles: MANAGERS, isNew: true, moduleId: 'report_dashboard' },
      { path: '/management-hub', icon: BarChart3, label: 'Management Hub', roles: ADMIN, moduleId: 'mgmt_hub' },
      { path: '/analytics/intelligence', icon: TrendingUp, label: 'Business Intelligence', roles: ADMIN, moduleId: 'bi' },
      { path: '/analytics/reports', icon: FileText, label: 'Custom Reports', roles: MANAGERS, moduleId: 'custom_reports' },
      { path: '/analytics/scheduled-reports', icon: Calendar, label: 'Scheduled Reports', roles: MANAGERS, isNew: true, moduleId: 'scheduled_reports' },
      { path: '/analytics/ai-insights', icon: Sparkles, label: 'AI Business Insights', roles: MANAGERS, isNew: true, moduleId: 'ai_insights' },
      { path: '/analytics/efficiency-roi', icon: TrendingUp, label: 'Efficiency & ROI', roles: ALL, isNew: true, moduleId: 'efficiency_roi' },
      { path: '/analytics/performance-loop', icon: TrendingUp, label: 'Performance Loop', roles: MANAGERS, isNew: true, audiences: OP_AUDIENCE, moduleId: 'performance_loop' },
      { path: '/usage', icon: Activity, label: 'Usage Analytics', roles: PLATFORM, moduleId: 'usage' },
      { path: '/analytics/goals', icon: Target, label: 'Goal Tracking', roles: MANAGERS, moduleId: 'goals' },
    ],
  },
  {
    id: 'settings',
    label: 'Platform Settings',
    icon: Settings,
    color: '#6B7280',
    items: [
      { path: '/settings', icon: Settings, label: 'Settings', roles: MANAGERS },
      { path: '/settings/security', icon: Lock, label: 'Security', roles: MANAGERS },
      { path: '/settings/global', icon: Globe, label: 'Global Settings', roles: ADMIN },
      { path: '/settings/config', icon: Sliders, label: 'Configuration Assistant', roles: MANAGERS, isNew: true, moduleId: 'global_settings' },
      { path: '/settings/form-builder', icon: FileText, label: 'Form Builder', roles: MANAGERS, isNew: true },
      { path: '/settings/ad-accounts', icon: Key, label: 'Integrations', roles: TENANTS, isNew: true, audiences: OP_AUDIENCE },
      { path: '/settings/credits', icon: Coins, label: 'Top Up Credits', roles: MANAGERS, isNew: true },
      { path: '/settings/whitelabel', icon: Palette, label: 'Whitelabel Management', roles: PLATFORM, isNew: true },
      { path: '/settings/landing-editor', icon: Sparkles, label: 'Landing Page Editor', roles: PLATFORM, audiences: PLAT_AUDIENCE, isNew: true },
      { path: '/automation-center', icon: Zap, label: 'Automation Center', roles: MANAGERS, audiences: OP_AUDIENCE, isNew: true, moduleId: 'automation-engine' },
      // v2.5.927 — Setup Wizard moved to Profile dropdown (Header.jsx). Line kept
      // commented as a reference so future contributors don't accidentally re-add.
      // { path: '/onboarding', icon: Rocket, label: 'Setup Wizard', roles: MANAGERS, audiences: OP_AUDIENCE, isNew: true, moduleId: 'onboarding' },
      { path: '/settings/my-whitelabel', icon: Sparkles, label: 'My Whitelabel', roles: TENANTS, enterprise: true, isNew: true, audiences: OP_AUDIENCE },
      { path: '/settings/letterheads', icon: FileText, label: 'Letterhead System', roles: MANAGERS, isNew: true, moduleId: 'letterheads' },
      { path: '/settings/audit-log', icon: Shield, label: 'Audit Log', roles: PLATFORM },
      { path: '/settings/data-export', icon: FolderOpen, label: 'Data Export', roles: MANAGERS },
      { path: '/settings/migration', icon: Truck, label: 'Migration Wizard', roles: ADMIN, enterprise: true },
      { path: '/settings/api-health', icon: Activity, label: 'API Integration Health', roles: PLATFORM, isNew: true },
      { path: '/dev-console', icon: Code2, label: 'VibeKonnect', roles: PLATFORM, isNew: true },
      { path: '/settings/system-health', icon: HeartPulse, label: 'System Health', roles: ADMIN, isNew: true },
      { path: '/settings/deploy-health', icon: HeartPulse, label: 'Deploy Health', roles: ['super_admin'], isNew: true },
      { path: '/settings/addon-grants', icon: ShieldCheck, label: 'Add-on Grants', roles: ['super_admin'], isNew: true },
      { path: '/settings/reference-data', icon: Database, label: 'Reference Data', roles: ['super_admin'], isNew: true },
      { path: '/settings/release-notes', icon: Sparkles, label: 'Release Notes', roles: PLATFORM, isNew: true },
    ],
  },
];

const STORAGE_KEY = 'konnect_sidebar_v2';

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, effectiveRole, isViewingAs, viewAsRole, setViewAsRole, showAllFeatures, setShowAllFeatures } = useAuth();
  const { enabledModules, activeModules, availableModules, lockedModules } = useModules();
  const location = useLocation();
  const navigate = useNavigate();

  // Load collapsed state from localStorage — default ALL collapsed
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    const defaults = {};
    sidebarSections.forEach(s => { defaults[s.id] = true; });
    return defaults;
  });

  const toggleSection = (id) => {
    setCollapsed(prev => {
      // If this section is already open, just close it
      if (!prev[id]) {
        const next = { ...prev, [id]: true };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      }
      // Otherwise collapse ALL, then open only the clicked one
      const next = {};
      sidebarSections.forEach(s => { next[s.id] = s.id !== id; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-500';
      case 'growth': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const roleLabel = {
    super_admin: 'Super Admin',
    country_operator: 'Country Operator',
    client_admin: 'Subscriber Admin',
    team_member: 'Employee',
  };

  const role = effectiveRole || 'client_admin';

  // v3.16 — GM / Manager / Director elevation for team_member users.
  const _MGR_KEYWORDS = ['general manager','gm','director','head of','lead','manager','vp','chief'];
  const _pos = String(user?.position || user?.job_title || user?.employee?.position || '').toLowerCase();
  const isElevatedEmployee = role === 'team_member' && (
    user?.is_elevated_employee === true ||
    _MGR_KEYWORDS.some(k => _pos.includes(k))
  );

  // Listen for whitelabel brand updates from ChromeBrandProvider
  const [brandLogo, setBrandLogo] = useState(() => (typeof window !== 'undefined' && window.__KONNECT_BRAND_LOGO) || null);
  useEffect(() => {
    const handler = (e) => setBrandLogo(e.detail || null);
    window.addEventListener('konnect:brand-logo', handler);
    return () => window.removeEventListener('konnect:brand-logo', handler);
  }, []);

  // Live backend version — fetched once per mount. Compares against
  // the compiled-in frontend PLATFORM_VERSION to expose deploy drift.
  const [backendVersion, setBackendVersion] = useState('');
  const [backendCommit, setBackendCommit] = useState('');
  useEffect(() => {
    let mounted = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (mounted && d) {
          setBackendVersion(d.version || '');
          setBackendCommit((d.commit || '').slice(0, 7));
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  // v2.5.923 — Tolerate a 1-patch-version drift silently (auto-version bumps
  // once more after yarn build). Only flag drift for ≥ 2 patch versions or
  // any major/minor difference.
  const _versionsFarApart = (a, b) => {
    try {
      const pa = String(a).split('.').map(n => parseInt(n, 10) || 0);
      const pb = String(b).split('.').map(n => parseInt(n, 10) || 0);
      if (pa[0] !== pb[0] || pa[1] !== pb[1]) return true;
      return Math.abs((pa[2] || 0) - (pb[2] || 0)) >= 2;
    } catch { return true; }
  };
  const versionDrift = !!backendVersion && _versionsFarApart(backendVersion, PLATFORM_VERSION);

  // v2.5.883 — Companion sidebar-entry visibility toggle (Platform Owner / Enterprise
  // can hide the DEX Companion link from the sidebar while keeping /vending/companion
  // reachable directly, or vice versa). Uses axios (bearer token) — NOT fetch(credentials:'include')
  // which trips CORS wildcards.
  const [companionSidebarEnabled, setCompanionSidebarEnabled] = useState(true);
  useEffect(() => {
    let mounted = true;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/vending/companion/settings`;
    // Only fetch if we have a token — anonymous fetches spam the console.
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) return;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (mounted && d && d.settings) {
          setCompanionSidebarEnabled(d.settings.sidebar_entry_enabled !== false);
        }
      })
      .catch(() => { /* silent — sidebar visibility is a non-critical hint */ });
    return () => { mounted = false; };
  }, []);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden animate-fadeIn"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-slate-dark z-50 transform transition-transform duration-300 ease-in-out flex flex-col",
          "md:translate-x-0 md:z-40 md:top-16 md:h-[calc(100vh-4rem)]",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Mobile close button */}
        <div className="flex items-center justify-between p-4 md:hidden flex-shrink-0">
          <div className="flex items-center gap-2">
            {brandLogo
              ? <img src={brandLogo} alt="Brand" className="h-8 w-auto object-contain max-w-[140px]" data-testid="sidebar-brand-logo" />
              : <img src="/konnect-logo-white.png" alt="Konnect" className="h-8 w-auto object-contain" />}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-light hover:bg-white/10"
            data-testid="sidebar-close-btn"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* User info */}
        <div className="px-4 py-3 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-emerald-brand flex-shrink-0">
              <AvatarFallback className="bg-emerald-brand text-white">
                {getInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.full_name || 'User'}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <PlanBadge user={user} />
                <span className="text-[10px] text-gray-400">{roleLabel[user?.role] || user?.role}</span>
              </div>
            </div>
          </div>
        </div>

        {/* View-As Switcher (super_admin only) */}
        {user?.role === 'super_admin' && (
          <div className="px-3 py-2 border-b border-gray-700 flex-shrink-0" data-testid="view-as-switcher">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 px-1">View as</p>
            <div className="flex flex-col gap-0.5">
              {[
                { role: null, label: 'Super Admin' },
                { role: 'country_operator', label: 'Country Operator' },
                { role: 'client_admin', label: 'Subscriber Admin' },
                { role: 'team_member', label: 'Employee' },
              ].map(({ role: r, label }) => {
                const isActive = viewAsRole === r || (!viewAsRole && r === null);
                return (
                  <button
                    key={label}
                    onClick={() => setViewAsRole(r)}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors text-left",
                      isActive ? "bg-emerald-brand/20 text-emerald-brand" : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                    )}
                    data-testid={`view-as-${(r || 'super_admin').replace('_', '-')}`}
                  >
                    <Eye className="h-3 w-3 flex-shrink-0" />
                    <span>{label}</span>
                    {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-brand" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Show-All-Features toggle (visible for super_admin and country_operator only) */}
        {(role === 'super_admin' || role === 'country_operator') && (
          <div className="px-3 py-2 border-b border-gray-700 flex-shrink-0">
            <button
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              className={cn(
                "w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-xs transition-colors",
                showAllFeatures ? "bg-emerald-brand/15 text-emerald-brand" : "text-gray-400 hover:bg-white/5 hover:text-gray-200"
              )}
              data-testid="toggle-show-all-features"
              title={showAllFeatures
                ? "Showing every feature including subscriber-facing modules"
                : "Showing only features for your role. Click to reveal subscriber-facing modules too."}
            >
              <span className="flex items-center gap-2">
                <Sparkles className="h-3 w-3 flex-shrink-0" />
                <span>{showAllFeatures ? 'Showing all features' : 'Clean view'}</span>
              </span>
              <span className={cn(
                "px-1.5 py-0.5 rounded-full text-[9px] font-semibold",
                showAllFeatures ? "bg-emerald-brand/30 text-emerald-brand" : "bg-gray-700 text-gray-400"
              )}>
                {showAllFeatures ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>
        )}

        {/* View-As Banner */}
        {isViewingAs && (
          <div className="mx-3 mt-2 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 flex-shrink-0" data-testid="view-as-banner">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-amber-400">
                Viewing as <span className="font-semibold">{roleLabel[viewAsRole]}</span>
              </p>
              <button onClick={() => setViewAsRole(null)} className="text-[10px] text-amber-400 hover:text-amber-300 underline" data-testid="exit-view-as-btn">
                Exit
              </button>
            </div>
          </div>
        )}

        {/* Sectioned Navigation */}
        <nav className="px-2 pt-2 pb-0 overflow-y-auto flex-1 sidebar-scroll" data-testid="sidebar-nav">
          {sidebarSections.map((section) => {
            const isAdminAudience = role === 'super_admin' || role === 'country_operator';
            const neededAudience = role === 'super_admin' ? 'platform' : 'operator';
            const visibleItems = section.items.filter(item => {
              // v2.5.883 — Hide companion link when Platform Owner/Enterprise has
              // disabled the sidebar entry (standalone route still reachable).
              if (item.companionOnly && !companionSidebarEnabled) return false;
              if (!(item.roles.includes(role) || (isElevatedEmployee && (item.roles.includes('client_admin') || item.roles.includes('country_operator'))))) return false;
              // `enterprise: true` items hide for non-enterprise client_admins
              // (super_admin and country_operator always see them).
              if (item.enterprise && role === 'client_admin' && (user?.plan || '').toLowerCase() !== 'enterprise') {
                return false;
              }
              // Audience filter — clean view for admins. When `showAllFeatures` is ON,
              // admins see every role-allowed item (no audience filtering).
              // When OFF: admin only sees items whose audience matches their role.
              // Items without an `audiences` tag are subscriber-facing tools → hidden
              // from admins in clean view, EXCEPT items that are role-restricted to
              // PLATFORM only (e.g. Audit Log, Usage Analytics) which are implicitly
              // platform-audience and always shown to super_admin.
              if (isAdminAudience && !showAllFeatures) {
                const isImplicitPlatform =
                  role === 'super_admin' && item.roles.length === 1 && item.roles[0] === 'super_admin';
                if (!isImplicitPlatform) {
                  if (!item.audiences) return false;
                  if (!item.audiences.includes(neededAudience)) return false;
                }
              }
              return true;
            });
            if (visibleItems.length === 0) return null;

            // v2.5.867 — For strict add-on sections (Vending), hide the entire
            // section when the user has no access to ANY of its items. This
            // prevents non-purchased subscribers from seeing a teaser-with-padlocks
            // menu they explicitly should not see.
            if (section.hideWhenAllLocked) {
              const anyReachable = visibleItems.some(item => {
                const st = getModuleState(activeModules, availableModules, lockedModules, item.moduleId);
                return st !== 'locked';
              });
              if (!anyReachable) return null;
            }

            const isCollapsed = collapsed[section.id];
            const hasActive = visibleItems.some(
              item => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
            );

            return (
              <div key={section.id} className="mb-0.5" data-testid={`sidebar-section-${section.id}`}>
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors group",
                    hasActive && isCollapsed ? "bg-white/5" : "hover:bg-white/5"
                  )}
                  data-testid={`sidebar-section-toggle-${section.id}`}
                >
                  <div
                    className="w-1 h-4 rounded-full flex-shrink-0 transition-opacity"
                    style={{ backgroundColor: section.color, opacity: hasActive ? 1 : 0.3 }}
                  />
                  <section.icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: section.color }} />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 group-hover:text-gray-300 flex-1 truncate">
                    {section.label}
                  </span>
                  {isCollapsed
                    ? <ChevronRight className="h-3 w-3 text-gray-500" />
                    : <ChevronDown className="h-3 w-3 text-gray-500" />}
                </button>

                {/* Section Items */}
                {!isCollapsed && (
                  <div className="ml-3 pl-2 border-l border-gray-700/50 space-y-0.5 pb-1">
                    {visibleItems.map((item) => {
                      // v2.5.897 — Action items (no path, just an onClick).
                      // Currently used for "Replay Vending Tour" in the Vending section.
                      if (item.action === 'replay-vending-tour') {
                        return (
                          <button
                            key="replay-vending-tour"
                            onClick={async () => {
                              try {
                                const axios = (await import('axios')).default;
                                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tour/steps/vending/reset`);
                              } catch { /* backend may 404 on stale deploys — replay client-side */ }
                              try { sessionStorage.removeItem('vending_tour_dismissed'); } catch { /* sessionStorage may be blocked */ }
                              onClose();
                              if (window.location.pathname.startsWith('/vending')) {
                                window.dispatchEvent(new CustomEvent('vending-tour-replay'));
                              } else {
                                navigate('/vending');
                                setTimeout(() => window.dispatchEvent(new CustomEvent('vending-tour-replay')), 400);
                              }
                            }}
                            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-body transition-colors text-sky-300 hover:bg-sky-500/10 hover:text-sky-200"
                            data-testid="sidebar-replay-vending-tour"
                          >
                            <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{item.label}</span>
                          </button>
                        );
                      }
                      const modState = getModuleState(activeModules, availableModules, lockedModules, item.moduleId);
                      const isLocked = modState === 'locked';
                      const isAvailable = modState === 'available';

                      if (isLocked) {
                        // v3.16 — For team_member: keep the item visible with a padlock so
                        // they know the feature exists, but the click informs them to
                        // contact their Subscriber Admin instead of jumping to a page they
                        // don't have permission to see.
                        const lockHandler = role === 'team_member'
                          ? () => { import('sonner').then(m => m.toast.info('Ask your Subscriber Admin to enable this module for you.')).catch(()=>{}); }
                          : () => { onClose(); navigate('/settings/module-manager?tab=shop'); };
                        return (
                          <button
                            key={item.path}
                            onClick={lockHandler}
                            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-body transition-colors text-gray-600 hover:bg-white/5 hover:text-gray-500 cursor-pointer"
                            data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
                            title="Upgrade to unlock this module"
                          >
                            <item.icon className="h-3.5 w-3.5 flex-shrink-0 opacity-40" />
                            <span className="truncate opacity-50">{item.label}</span>
                            <Lock className="h-3 w-3 text-gray-600 ml-auto flex-shrink-0" />
                          </button>
                        );
                      }

                      if (isAvailable) {
                        return (
                          <button
                            key={item.path}
                            onClick={() => { onClose(); navigate('/settings/module-manager'); }}
                            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-body transition-colors text-gray-500 hover:bg-white/5 hover:text-gray-400 cursor-pointer"
                            data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
                            title="Click to activate this module"
                          >
                            <item.icon className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
                            <span className="truncate opacity-60">{item.label}</span>
                            <Badge className="bg-gray-600/40 text-[7px] px-1 py-0 h-3.5 ml-auto text-gray-400">OFF</Badge>
                          </button>
                        );
                      }

                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={({ isActive }) => cn(
                            "flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-body transition-colors",
                            isActive
                              ? "bg-emerald-brand/10 text-emerald-brand"
                              : "text-gray-400 hover:bg-white/5 hover:text-gray-200"
                          )}
                          data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
                        >
                          <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                          {item.enterprise && <Badge className="bg-purple-500/80 text-[7px] px-1 py-0 h-3.5 ml-auto">PRO</Badge>}
                          {item.isNew && <Badge className="bg-blue-500/80 text-[7px] px-1 py-0 h-3.5 ml-auto">NEW</Badge>}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Version info — v2.5.914+ FINALITY FIX:
              The sidebar now displays the BACKEND's live /api/version as the
              primary number. The compiled-in PLATFORM_VERSION was the source of
              "3 different versions across preview/prod/code" confusion — a stale
              JS bundle would show an ancient version even when the backend was
              current. Now we always show what's ACTUALLY RUNNING. */}
          <div className="pt-3 pb-20 md:pb-4 mt-2 border-t border-gray-700/50" data-testid="sidebar-footer">
            <NavLink to="/settings/release-notes" className="block text-center group cursor-pointer hover:opacity-80 transition-opacity">
              <p className="text-[10px] text-gray-500 tracking-wide">
                Konnect <span className="text-emerald-500/70 group-hover:text-emerald-400 transition-colors" data-testid="sidebar-version-display">
                  v{backendVersion || PLATFORM_VERSION}
                </span>
                {user?.role === 'super_admin' && backendVersion && versionDrift && (
                  <span className="ml-1.5 text-amber-400 group-hover:text-amber-300 transition-colors"
                    title={`Your browser is running bundle v${PLATFORM_VERSION} but backend is v${backendVersion}. Auto-updater will refresh your bundle shortly. (Backend commit: ${backendCommit})`}
                    data-testid="sidebar-backend-version">
                    · bundle v{PLATFORM_VERSION} refreshing…
                  </span>
                )}
                {user?.role === 'super_admin' && backendVersion && !versionDrift && (
                  <span className="ml-1.5 text-emerald-500/70 group-hover:text-emerald-400 transition-colors"
                    title={`✅ Live: backend v${backendVersion} · bundle v${PLATFORM_VERSION} · commit ${backendCommit}`}
                    data-testid="sidebar-backend-version">
                    · ✓ live
                  </span>
                )}
              </p>
              <p className="text-[9px] text-gray-600 mt-0.5">&copy; {new Date().getFullYear()} Kaharian Integrated Business Solutions</p>
            </NavLink>
            {user?.role === 'super_admin' && versionDrift && (
              <button
                onClick={() => {
                  // Nuke SW cache + hard-reload — recovers a stuck deploy in one click
                  if (navigator.serviceWorker?.controller) {
                    navigator.serviceWorker.controller.postMessage('KONNECT_SW_PURGE');
                  }
                  setTimeout(() => window.location.reload(), 300);
                }}
                className="mt-1 mx-auto block text-[10px] text-amber-400 hover:text-amber-300 underline underline-offset-2"
                data-testid="sidebar-force-refresh"
              >
                Force refresh
              </button>
            )}
            {/* Deploy Truth button — visible to super_admin only. One click and
                you see exactly which git SHA is running + template count. */}
            {user?.role === 'super_admin' && (
              <button
                onClick={async () => {
                  try {
                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version`, { cache: 'no-store' });
                    const d = await r.json();
                    const msg = [
                      `Backend commit:  ${d.commit || 'unknown'}`,
                      `Started at:      ${d.backend_started_at || 'unknown'}`,
                      `Templates loaded: ${d.automation_templates_loaded ?? '?'} / ${d.expected_templates ?? 32}`,
                      `Frontend build:   v${PLATFORM_VERSION}`,
                      '',
                      d.healthy === false
                        ? '⚠  UNHEALTHY: backend is running stale code. Re-run deploy-prod.sh.'
                        : '✓ Deploy is truly live.'
                    ].join('\n');
                    window.alert(msg);
                  } catch {
                    window.alert('Could not reach /api/version — backend is unreachable or on an old build.');
                  }
                }}
                className="mt-1 mx-auto block text-[10px] text-gray-500 hover:text-emerald-400 underline underline-offset-2"
                data-testid="sidebar-deploy-truth"
                title="Show exactly what commit and template count is running on the backend"
              >
                Deploy truth
              </button>
            )}
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
